import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { WalletSummaryResponse } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { resolveAppUrl, useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { formatActorName, formatCoins, formatUsdCents } from '@fetmode/utils';
import { Button, ConfirmModal } from '@fetmode/ui';

type WalletTab = 'earn' | 'spend';

const consumerUrl = resolveAppUrl('consumer', import.meta.env);

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '0.5rem',
  color: 'var(--fm-color-text-muted)',
} as const;

export function WalletPage(): ReactElement {
  const { user, api } = useAuth();
  const [wallet, setWallet] = useState<WalletSummaryResponse | null>(null);
  const [activeTab, setActiveTab] = useState<WalletTab>('earn');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState<string | null>(null);
  const [isReleasing, setIsReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [releaseSuccess, setReleaseSuccess] = useState<string | null>(null);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);

  const loadWallet = useCallback(async () => {
    const result = await api.getWallet();
    setWallet(result);
  }, [api]);

  useEffect(() => {
    let cancelled = false;

    void loadWallet()
      .catch(() => {
        if (!cancelled) {
          setError(t('common.studio.wallet.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loadWallet]);

  const earn = wallet?.earn ?? { pendingCents: 0, availableCents: 0 };
  const totalEarnCents = earn.pendingCents + earn.availableCents;

  async function handleWithdraw(): Promise<void> {
    const amountCents = Math.round(Number.parseFloat(withdrawAmount) * 100);
    if (!Number.isFinite(amountCents) || amountCents < 1) {
      setWithdrawError(t('common.studio.wallet.withdrawInvalid'));
      return;
    }
    if (amountCents > earn.availableCents) {
      setWithdrawError(t('common.studio.wallet.withdrawExceedsAvailable'));
      return;
    }

    setIsWithdrawing(true);
    setWithdrawError(null);
    setWithdrawSuccess(null);
    try {
      const result = await api.withdrawEarn({ amountCents });
      setWallet((current) =>
        current ? { ...current, earn: result.earn } : current,
      );
      setWithdrawAmount('');
      setWithdrawSuccess(t('common.studio.wallet.withdrawSuccess'));
    } catch (err) {
      setWithdrawError(
        err instanceof ApiError ? err.message : t('common.studio.wallet.withdrawError'),
      );
    } finally {
      setIsWithdrawing(false);
    }
  }

  async function handleReleasePending(): Promise<void> {
    setReleaseConfirmOpen(false);
    setIsReleasing(true);
    setReleaseError(null);
    setReleaseSuccess(null);
    try {
      const result = await api.releasePendingEarn();
      setWallet((current) =>
        current ? { ...current, earn: result.earn } : current,
      );
      setReleaseSuccess(t('common.studio.wallet.releaseSuccess'));
    } catch (err) {
      setReleaseError(
        err instanceof ApiError ? err.message : t('common.studio.wallet.releaseError'),
      );
    } finally {
      setIsReleasing(false);
    }
  }

  return (
    <section>
      <h1 style={{ fontFamily: 'var(--fm-font-heading)', marginTop: 0 }}>
        {t('common.studio.wallet.title')}
      </h1>
      <p style={{ color: 'var(--fm-color-text-muted)' }}>
        {t('common.auth.signedInAs')}{' '}
        {user
          ? formatActorName({
              username: user.username,
              displayName: user.displayName,
              email: user.email,
            })
          : ''}
      </p>

      {isLoading && (
        <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.wallet.loading')}</p>
      )}
      {error && <p className="fm-error">{error}</p>}

      {wallet && (
        <>
          <div className="fm-segment-group" style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className={activeTab === 'earn' ? 'fm-segment fm-segment--active' : 'fm-segment'}
              onClick={() => {
                setActiveTab('earn');
              }}
            >
              {t('common.studio.wallet.earnTab')}
            </button>
            <button
              type="button"
              className={activeTab === 'spend' ? 'fm-segment fm-segment--active' : 'fm-segment'}
              onClick={() => {
                setActiveTab('spend');
              }}
            >
              {t('common.studio.wallet.spendTab')}
            </button>
          </div>

          {activeTab === 'earn' ? (
            <div className="fm-card" style={{ marginTop: '1rem' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
                {t('common.studio.wallet.earnTotal')}
              </p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem' }}>
                {formatUsdCents(totalEarnCents)}
              </p>
              <div style={rowStyle}>
                <span>{t('common.studio.wallet.available')}</span>
                <span>{formatUsdCents(earn.availableCents)}</span>
              </div>
              <div style={rowStyle}>
                <span>{t('common.studio.wallet.pending')}</span>
                <span>{formatUsdCents(earn.pendingCents)}</span>
              </div>
              <p
                style={{
                  margin: '1rem 0 0',
                  fontSize: '0.875rem',
                  color: 'var(--fm-color-text-muted)',
                }}
              >
                {t('common.studio.wallet.earnHint')}
              </p>
              <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                {releaseError ? <p className="fm-error">{releaseError}</p> : null}
                {releaseSuccess ? (
                  <p style={{ margin: 0, color: 'var(--fm-color-secondary)', fontSize: '0.875rem' }}>
                    {releaseSuccess}
                  </p>
                ) : null}
                <Button
                  label={
                    isReleasing
                      ? t('common.studio.wallet.releasing')
                      : t('common.studio.wallet.releasePending')
                  }
                  disabled={isReleasing || earn.pendingCents < 1}
                  onClick={() => {
                    setReleaseConfirmOpen(true);
                  }}
                />
              </div>
              <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
                <label>
                  {t('common.studio.wallet.withdrawAmount')}
                  <input
                    className="fm-input"
                    type="number"
                    min={0.01}
                    step={0.01}
                    value={withdrawAmount}
                    disabled={isWithdrawing}
                    onChange={(event) => {
                      setWithdrawAmount(event.target.value);
                      setWithdrawError(null);
                      setWithdrawSuccess(null);
                    }}
                    placeholder={t('common.studio.wallet.withdrawPlaceholder')}
                    style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
                  />
                </label>
                {withdrawError ? <p className="fm-error">{withdrawError}</p> : null}
                {withdrawSuccess ? (
                  <p style={{ margin: 0, color: 'var(--fm-color-secondary)', fontSize: '0.875rem' }}>
                    {withdrawSuccess}
                  </p>
                ) : null}
                <Button
                  label={
                    isWithdrawing
                      ? t('common.studio.wallet.withdrawing')
                      : t('common.studio.wallet.withdraw')
                  }
                  disabled={isWithdrawing || earn.availableCents < 1}
                  onClick={() => {
                    void handleWithdraw();
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="fm-card" style={{ marginTop: '1rem' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
                {t('common.studio.wallet.spendBalance')}
              </p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem' }}>
                {formatCoins(wallet.spend.total)}
              </p>
              <div style={rowStyle}>
                <span>{t('common.studio.wallet.purchased')}</span>
                <span>{formatCoins(wallet.spend.purchased)}</span>
              </div>
              <div style={rowStyle}>
                <span>{t('common.studio.wallet.bonus')}</span>
                <span>{formatCoins(wallet.spend.bonus)}</span>
              </div>
              <div style={rowStyle}>
                <span>{t('common.studio.wallet.gifted')}</span>
                <span>{formatCoins(wallet.spend.gifted)}</span>
              </div>
              <p style={{ margin: '1rem 0 0', fontSize: '0.875rem' }}>
                <a href={`${consumerUrl}/wallet`}>{t('common.studio.wallet.buyOnConsumer')}</a>
              </p>
            </div>
          )}
        </>
      )}
      <ConfirmModal
        open={releaseConfirmOpen}
        title={t('common.dialog.title')}
        message={t('common.studio.wallet.releaseConfirm').replace(
          '{{amount}}',
          formatUsdCents(earn.pendingCents),
        )}
        confirmLabel={
          isReleasing
            ? t('common.studio.wallet.releasing')
            : t('common.studio.wallet.releasePending')
        }
        cancelLabel={t('common.dialog.cancel')}
        confirmVariant="request"
        busy={isReleasing}
        onCancel={() => {
          if (!isReleasing) {
            setReleaseConfirmOpen(false);
          }
        }}
        onConfirm={() => {
          void handleReleasePending();
        }}
      />
    </section>
  );
}
